@let type = stopType();
@let form = formState();
@let timecard = selectedTimecard();
@let isTask = isTaskStop();
@let sourceType = locationSourceType();

<bz-dialog-layout [heading]="'Add Stop' | translate" [dismissible]="true">

  <div bzDialogContent class="layout-column timecard-dialog-body" appBringozIdGenerator="addStopDialog">
    @if (timecards.length > 1) {
      <bz-select size="sm"
                 appBringozIdGenerator="timecardSelect"
                 [label]="'Timecard' | translate"
                 [placeholder]="'Select Timecard' | translate"
                 [options]="timecardOptions"
                 [value]="selectedTimecardId()"
                 (valueChange)="onTimecardSelect($event)">
      </bz-select>
    }

    <bz-select size="sm"
               appBringozIdGenerator="stopTypeSelect"
               [label]="'Stop Type' | translate"
               [options]="stopTypeOptions()"
               [value]="type"
               (valueChange)="onStopTypeChange($event)">
    </bz-select>

    @if (type === StopType.Task) {
      <bz-form-field [label]="'Account' | translate">
        <bz-button class="account-select-trigger"
                   variant="outline"
                   appBringozIdGenerator="accountSelectBtn"
                   [ariaLabel]="'Account' | translate"
                   (clicked)="onOpenLocationPanel($event)">
          <span class="flex text-ellipsis account-select-text" [class.account-select-placeholder]="!selectedAccount()">
            {{ selectedAccount()?.name || ('Account' | translate) }}
          </span>
          <bz-icon [icon]="icons.chevron"></bz-icon>
        </bz-button>
      </bz-form-field>

      @if (existingEntryIndex() !== null) {
        <div class="timecard-dialog-info">
          {{ 'Account already exists in this timecard. Arrival and departure times will be updated.' | translate }}
        </div>
      }
    }

    @if (type === StopType.CustomTask) {
      <!-- Not a bz-form-field: its <label> would forward a click on the caption to the first toggle button -->
      <div class="location-source-field">
        <span class="location-source-caption">{{ 'Address' | translate }}</span>

        <div class="timecard-dialog-row">
          <div class="location-source-toggle" role="group" [attr.aria-label]="'Address' | translate">
            @for (source of locationSources; track source.type) {
              <bz-button variant="tertiary"
                         tone="secondary"
                         size="fit"
                         class="location-source-option"
                         [class.location-source-option-active]="source.type === sourceType"
                         [appBringozIdGenerator]="'locationSource' + source.type"
                         [ariaLabel]="source.label | translate"
                         [ngbTooltip]="source.label | translate"
                         container="body"
                         (clicked)="onLocationSourceChange(source.type)">
                <bz-icon [icon]="source.icon"></bz-icon>
              </bz-button>
            }
          </div>

          @if (sourceType === LocationSourceType.CUSTOM) {
            <app-address-autocomplete-input class="flex"
                                            [address]="customAddress()"
                                            (onChange)="onCustomAddressChange($event)">
            </app-address-autocomplete-input>
          } @else {
            <bz-button class="flex account-select-trigger"
                       variant="outline"
                       appBringozIdGenerator="locationSourceSelectBtn"
                       [ariaLabel]="(sourceType === LocationSourceType.SERVICE_POINT ? 'Service Location' : 'Account') | translate"
                       (clicked)="onOpenLocationPanel($event)">
              <span class="flex text-ellipsis account-select-text" [class.account-select-placeholder]="!locationSourceName()">
                {{ locationSourceName() || ((sourceType === LocationSourceType.SERVICE_POINT ? 'Service Location' : 'Account') | translate) }}
              </span>
              <bz-icon [icon]="icons.chevron"></bz-icon>
            </bz-button>
          }
        </div>
      </div>
    }

    @if (type === StopType.Break) {
      <bz-select size="sm"
                 appBringozIdGenerator="breakTypeSelect"
                 [label]="'Break Type' | translate"
                 [placeholder]="'Break Type' | translate"
                 [options]="breakTypeOptions()"
                 [value]="selectedBreakTypeId()"
                 [disabled]="isLoadingBreakTypes()"
                 (valueChange)="onBreakTypeSelect($event)">
      </bz-select>

      @if (duplicateBreakTypeWarning()) {
        <div class="timecard-dialog-warning">
          <bz-icon size="sm" [icon]="icons.warning"></bz-icon>
          <span>{{ 'DUPLICATE_BREAK_TYPE_WARNING' | translate }}</span>
        </div>
      }
    }

    @if (isTask || type === StopType.Break) {
      <bz-form-field [label]="(type === StopType.Break ? 'Break Start' : 'Arrival') | translate">
        <app-timecard-date-time-field [timeStamp]="arrivalTime()"
                                      [referenceDate]="referenceDate()"
                                      [zoneId]="data.zoneId"
                                      [minTime]="timecard?.clockInTime"
                                      [maxTime]="timecard?.clockOutTime"
                                      (timeChange)="onArrivalChange($event)">
        </app-timecard-date-time-field>
      </bz-form-field>

      <bz-form-field [label]="(type === StopType.Break ? 'Break End' : 'Departure') | translate">
        <app-timecard-date-time-field [timeStamp]="departureTime()"
                                      [referenceDate]="referenceDate()"
                                      [zoneId]="data.zoneId"
                                      [minTime]="timecard?.clockInTime"
                                      [maxTime]="timecard?.clockOutTime"
                                      (timeChange)="onDepartureChange($event)">
        </app-timecard-date-time-field>
      </bz-form-field>
    }

    @if (isTask) {
      <bz-form-field [label]="('Service Time' | translate) + ' ' + ('(optional)' | translate)">
        <div class="timecard-dialog-row">
          <bz-input class="flex"
                    type="number"
                    size="sm"
                    appBringozIdGenerator="serviceHoursInput"
                    [ariaLabel]="'Hour' | translate"
                    [value]="serviceHours()"
                    (valueChange)="serviceHours.set($event)">
            <span bzInputSuffix>{{ 'Hour' | translate }}</span>
          </bz-input>
          <bz-input class="flex"
                    type="number"
                    size="sm"
                    appBringozIdGenerator="serviceMinutesInput"
                    [ariaLabel]="'Min' | translate"
                    [value]="serviceMinutes()"
                    (valueChange)="serviceMinutes.set($event)">
            <span bzInputSuffix>{{ 'Min' | translate }}</span>
          </bz-input>
        </div>
      </bz-form-field>
    }

    @if (type === StopType.AgentStartLocation || type === StopType.AgentEndLocation) {
      <bz-form-field [label]="'Address' | translate">
        @if (hasAgentAddress) {
          <span class="br-body-2">{{ data.agentAddress | bringozAddress:true }}</span>
        } @else {
          <span class="br-body-2 gray-101">{{ 'No address available' | translate }}</span>
        }
      </bz-form-field>
    }

    @if (form.error) {
      <div class="timecard-dialog-error" role="alert">{{ form.error | translate }}</div>
    }
  </div>

  <div bzDialogActions>
    <bz-button variant="tertiary" appBringozIdGenerator="cancelBtn" (clicked)="onCancel()">
      {{ 'Cancel' | translate }}
    </bz-button>
    <bz-button tone="secondary" appBringozIdGenerator="submitBtn" [disabled]="!form.canSubmit" (clicked)="onSubmit()">
      {{ 'Submit' | translate }}
    </bz-button>
  </div>

</bz-dialog-layout>
